import React, { useState, useEffect } from 'react';
import {
  VStack,
  HStack,
  Text,
  Input,
  useMediaQuery,
  ScrollView
} from 'native-base';
import CustomButton from '../../../../components/button';
import { COLORS } from '../../../../utils/colors';
import { COMMONFONTSIZE, COMMONFONTWEIGHT } from '../../../../utils/fonts';
import { MOBILE_VIEW_WIDTH } from '../../../../utils/screens';
import CustomModal from '../../../../components/modal';
import MobileModelSheet from '../../../../components/actionSheet';

const BenchmarkEditModal = ({
  isOpen,
  onClose,
  editData,
  onSubmit,
  isEditing = false
}) => {
  const [MobileWindow] = useMediaQuery({ maxWidth: MOBILE_VIEW_WIDTH });

  // Form state
  const [formData, setFormData] = useState({
    qty: '',
    benchmark_price: '',
    avg_invoice_value: '',
    remarks: ''
  });

  // Validation state
  const [errors, setErrors] = useState({});

  // Initialize form data when editData changes
  useEffect(() => {
    if (editData) {
      setFormData({
        qty: editData.quantity || '',
        benchmark_price: editData.benchmark_price?.toString() || '',
        avg_invoice_value: editData.avg_invoice_value?.toString() || '',
        remarks: editData.remarks || ''
      });
      setErrors({});
    }
  }, [editData]);

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }

  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (formData.qty) {
      const qtyValue = Number(formData.qty);
      if (!Number.isFinite(qtyValue) || qtyValue <= 0) {
        newErrors.qty = 'Quantity must be greater than 0';
      }
    }

    if (!formData.benchmark_price || parseFloat(formData.benchmark_price) <= 0) {
      newErrors.benchmark_price = 'Benchmark price must be greater than 0';
    }

    if (formData.avg_invoice_value && parseFloat(formData.avg_invoice_value) < 0) {
      newErrors.avg_invoice_value = 'Avg. invoice value cannot be negative';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    const submitData = {
      qty: formData.qty.trim(),
      benchmark_price: parseFloat(formData.benchmark_price),
      remarks: formData.remarks.trim(),
      avg_invoice_value: formData.avg_invoice_value
        ? parseFloat(formData.avg_invoice_value)
        : null
    };

    onSubmit(submitData);
  };

  // Handle close
  const handleClose = () => {
    setFormData({
      qty: '',
      benchmark_price: '',
      avg_invoice_value: '',
      remarks: ''
    });
    setErrors({});
    onClose();
  };

  // Modal header
  const getModalHeader = () => {
    return <Text fontSize={'24px'} fontWeight={COMMONFONTWEIGHT.LARGE_FONT_WEIGHT}>Edit Benchmark Data</Text>;
  };

  // Modal body
  const getModalBody = () => {
    return (
      <ScrollView showsVerticalScrollIndicator={false}>
        <VStack space={4} p={MobileWindow ? 2 : 4}>
          {/* Quantity */}
          <VStack space={2}>
            <Text fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE} fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}>
              Quantity
            </Text>
            <Input
              placeholder="Enter quantity"
              value={formData.qty}
              onChangeText={(value) => handleInputChange('qty', value)}
              borderColor={errors.qty ? COLORS.REJECT_TEXT_COLOR : COLORS.BORDER_COLOR}
              _focus={{ borderColor: errors.qty ? COLORS.REJECT_TEXT_COLOR : COLORS.THEME_COLOR }}
              fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
            />
            {errors.qty && (
              <Text fontSize={COMMONFONTSIZE.SMALL_FONT_SIZE} color={COLORS.REJECT_TEXT_COLOR}>
                {errors.qty}
              </Text>
            )}
          </VStack>

          {/* Benchmark Price and Avg Invoice Value */}
          <HStack space={4}>
            <VStack space={2} flex={1}>
              <Text fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE} fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}>
                Benchmark Price (Rs.) *
              </Text>
              <Input
                placeholder="Enter benchmark price"
                value={formData.benchmark_price}
                onChangeText={(value) => handleInputChange('benchmark_price', value)}
                keyboardType="numeric"
                borderColor={errors.benchmark_price ? COLORS.REJECT_TEXT_COLOR : COLORS.BORDER_COLOR}
                _focus={{ borderColor: COLORS.THEME_COLOR }}
                fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
              />
              {errors.benchmark_price && (
                <Text fontSize={COMMONFONTSIZE.SMALL_FONT_SIZE} color={COLORS.REJECT_TEXT_COLOR}>
                  {errors.benchmark_price}
                </Text>
              )}
            </VStack>

            <VStack space={2} flex={1}>
              <Text fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE} fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}>
                Avg Invoice Value (Rs.)
              </Text>
              <Input
                placeholder="Enter avg invoice value"
                value={formData.avg_invoice_value}
                onChangeText={(value) => handleInputChange('avg_invoice_value', value)}
                keyboardType="numeric"
                borderColor={errors.avg_invoice_value ? COLORS.REJECT_TEXT_COLOR : COLORS.BORDER_COLOR}
                _focus={{ borderColor: COLORS.THEME_COLOR }}
                fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
              />
              {errors.avg_invoice_value && (
                <Text fontSize={COMMONFONTSIZE.SMALL_FONT_SIZE} color={COLORS.REJECT_TEXT_COLOR}>
                  {errors.avg_invoice_value}
                </Text>
              )}
            </VStack>
          </HStack>

          {/* Remarks */}
          <VStack space={2}>
            <Text fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE} fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}>
              Remarks
            </Text>
            <Input
              placeholder="Enter remarks"
              value={formData.remarks}
              onChangeText={(value) => handleInputChange('remarks', value)}
              borderColor={COLORS.BORDER_COLOR}
              _focus={{ borderColor: COLORS.THEME_COLOR }}
              fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
            />
          </VStack>
        </VStack>
      </ScrollView>
    );
  };

  // Modal footer
  const getModalFooter = () => {
    return (
      <HStack justifyContent="flex-end" width="100%" space={4}>
        <CustomButton
          text="Cancel"
          onPress={handleClose}
          isOutline
          bgColor={COLORS.BLACK_COLOR}
          textColor={COLORS.BLACK_COLOR}
        />
        <CustomButton
          text={isEditing ? "Updating..." : "Update"}
          onPress={handleSubmit}
          isLoading={isEditing}
          disabled={isEditing}
        />
      </HStack>
    );
  };

  return (
    <>
      {!MobileWindow ? (
        <CustomModal
          isOpen={isOpen}
          onClose={handleClose}
          header={getModalHeader()}
          body={getModalBody()}
          footer={getModalFooter()}
          maxW="600px"
          width="max-content"
        />
      ) : (
        <MobileModelSheet
          isOpen={isOpen}
          onClose={handleClose}
          header={getModalHeader()}
          body={getModalBody()}
          footer={getModalFooter()}
          maxH="90vh"
        />
      )}
    </>
  );
};

export default BenchmarkEditModal; 